import { toastr } from 'react-redux-toastr';
import gql from 'graphql-tag';

// SiteAdmin actions on the refund engine. Each proxies through this backend's
// admin* refund mutations (schema.js), which forward to asap-super-api — the
// single service that dispatches refunds. Authorization (superadmin or roleId
// ADMIN/ACCOUNTANT) is enforced by the engine itself; a viewer without money
// authority sees its 403 message in the toast.

const RESULT_FIELDS = `
  status errorMessage id refundStatus amount failureCode failureMessage
`;

// Money fields must be WHOLE positive integers, validated on the raw string:
// parseInt('1e5') is 1 — a silently wrong amount — so scientific notation,
// decimals, signs and stray characters are all rejected outright.
// Returns the integer, or null when the value is not a plain positive integer.
export function toPositiveInt(value) {
  const text = String(value == null ? '' : value).trim();
  if (!/^\d+$/.test(text)) return null;
  const parsed = Number(text);
  if (!Number.isSafeInteger(parsed) || parsed <= 0) return null;
  return parsed;
}

function invalidInput(message) {
  return async () => {
    toastr.error('Error!', message);
    return { status: 400, errorMessage: message };
  };
}

// Single-leg bearer override -> the engine's allocation input. Component
// follows the bearer's natural cost bucket; multi-leg splits stay API-only.
const BEARER_COMPONENTS = { shop: 'items', driver: 'delivery_fee', platform: 'goodwill' };

function bearerAllocations(bearer, amount) {
  if (!bearer || !BEARER_COMPONENTS[bearer]) return null;
  return [{ bearer, component: BEARER_COMPONENTS[bearer], amount }];
}

function runRefundAction(mutation, variables, successText) {
  return async (dispatch, getState, { client }) => {
    try {
      const { data } = await client.mutate({ mutation, variables });
      const payload = data && data[Object.keys(data)[0]];

      if (payload && payload.status === 200) {
        toastr.success('Success', successText);
        return { status: 200 };
      }
      const errorMessage = (payload && payload.errorMessage) || 'Oops! Something went wrong. Please try again.';
      toastr.error('Error!', errorMessage);
      return { status: 400, errorMessage };
    } catch (error) {
      console.error(error);
      toastr.error('Error!', 'Something went wrong');
      return { status: 400 };
    }
  };
}

export function approveRefundRequest(id, amount, bearer) {
  const parsedAmount = toPositiveInt(amount);
  if (parsedAmount == null) {
    return invalidInput('Amount must be a whole positive number of IDR.');
  }
  return runRefundAction(gql`
    mutation ($id: Int!, $amount: Int, $allocations: [RefundAllocationInputAdminType]) {
      adminApproveRefundRequest(id: $id, amount: $amount, allocations: $allocations) { ${RESULT_FIELDS} }
    }
  `, { id: parseInt(id, 10), amount: parsedAmount, allocations: bearerAllocations(bearer, parsedAmount) },
  'Refund approved and dispatched.');
}

export function rejectRefundRequest(id, notes) {
  return runRefundAction(gql`
    mutation ($id: Int!, $notes: String) {
      adminRejectRefundRequest(id: $id, notes: $notes) { ${RESULT_FIELDS} }
    }
  `, { id: parseInt(id, 10), notes: notes || null },
  'Refund request rejected.');
}

export function retryRefundRequest(id) {
  return runRefundAction(gql`
    mutation ($id: Int!) {
      adminRetryRefundRequest(id: $id) { ${RESULT_FIELDS} }
    }
  `, { id: parseInt(id, 10) },
  'Refund dispatch retried.');
}

export function resolveRefundRequest(id, outcome, providerRefundId) {
  return runRefundAction(gql`
    mutation ($id: Int!, $outcome: String!, $providerRefundId: String) {
      adminResolveRefundRequest(id: $id, outcome: $outcome, providerRefundId: $providerRefundId) { ${RESULT_FIELDS} }
    }
  `, { id: parseInt(id, 10), outcome, providerRefundId: providerRefundId || null },
  'Refund resolved.');
}

export function createRefundRequest({ referenceType, referenceId, requestKey, amount, reasonCode, reasonNotes, approveNow, bearer }) {
  const parsedReferenceId = toPositiveInt(referenceId);
  if (parsedReferenceId == null) {
    return invalidInput('Booking id must be a whole positive number.');
  }
  // Amount is required to dispatch now; a create-only request may leave it to
  // the approver.
  let parsedAmount = null;
  if (amount != null && String(amount).trim() !== '') {
    parsedAmount = toPositiveInt(amount);
    if (parsedAmount == null) {
      return invalidInput('Amount must be a whole positive number of IDR.');
    }
  }
  if (approveNow && parsedAmount == null) {
    return invalidInput('An amount is required to refund immediately.');
  }
  return runRefundAction(gql`
    mutation ($referenceType: String!, $referenceId: Int!, $requestKey: String!, $amount: Int, $reasonCode: String!, $reasonNotes: String, $approveNow: Int, $allocations: [RefundAllocationInputAdminType]) {
      adminCreateRefundRequest(referenceType: $referenceType, referenceId: $referenceId, requestKey: $requestKey, amount: $amount, reasonCode: $reasonCode, reasonNotes: $reasonNotes, approveNow: $approveNow, allocations: $allocations) { ${RESULT_FIELDS} }
    }
  `, {
    referenceType,
    referenceId: parsedReferenceId,
    requestKey,
    amount: parsedAmount,
    reasonCode,
    reasonNotes: reasonNotes || null,
    approveNow: approveNow ? 1 : 0,
    allocations: parsedAmount != null ? bearerAllocations(bearer, parsedAmount) : null,
  },
  approveNow ? 'Refund created and dispatched.' : 'Refund request created for review.');
}
